// =============================================================================
// Member detail (§7): profile, eligibility, current loans, payments, history.
//
// Two things earn their prominence here:
//   1. The eligibility banner, shared with Issue so the wording cannot drift.
//   2. "Record membership fee" — a member with no payment cannot borrow (§6
//      check 2), and this is the only place to fix that. When unpaid, it is the
//      loudest thing on the screen.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { money } from '../config/school.js';
import {
  getMember, getMemberSummary, checkEligibility, activeLoansFor,
  memberPayments, memberLoanHistory, recordPayment, renewLoan, returnCopy,
  setMemberStatus, branchSettings, listBankAccounts,
} from '../lib/api.js';
import { spinner } from '../lib/loading.js';
import { getStaff } from '../lib/auth.js';
import { eligibilityBanner, slotsLine, loanRow } from '../lib/member-panel.js';
import { emitDataChanged } from '../lib/events.js';

export function memberDetailScreen(root, memberId, { pay = false } = {}) {
  let member = null, summary = null, eligibility = null;
  let loans = [], payments = [], history = [], settings = null, banks = [];
  // Arriving from "Create member" (#/members/<id>/pay) opens the fee form
  // straight away — see member-form.js for why the fee is not on that form.
  let showPaymentForm = pay, showHistory = false;
  // Only on that first arrival, so the line does not reappear every time the
  // form is opened by hand later.
  let justCreated = pay;
  // The deactivate reason, asked for in the app rather than in an OS prompt().
  let confirmingDeactivate = false;
  // The loan awaiting confirmation, or the string 'all'. Null when nothing is.
  let pendingReturn = null;
  let busy = null;   // 'payment' | 'renew' | 'return' | 'status'

  const view = el('div', { class: 'screen' });
  mount(root, view);

  const isAdmin = () => getStaff()?.role === 'admin';

  async function load() {
    mount(view, spinner());
    try {
      member = await getMember(memberId);
      if (!member) { mount(view, el('p', { class: 'error' }, T.memberNotFound)); return; }
      [summary, eligibility, loans, payments, history, banks] = await Promise.all([
        getMemberSummary(memberId),
        checkEligibility(memberId),
        activeLoansFor(memberId),
        memberPayments(memberId),
        memberLoanHistory(memberId),
        // Needed only by the payment form, but fetched with everything else so
        // opening the form is instant rather than a second wait.
        listBankAccounts().catch(() => []),
      ]);
      settings = await branchSettings(member.branch_id).catch(() => null);
      render();
    } catch (err) {
      mount(view, el('p', { class: 'error' }, err.message),
        el('a', { class: 'btn', href: '#/members' }, T.backToMembers));
    }
  }

  function render() {
    const unpaid = !summary?.has_paid;

    mount(view,
      el('a', { class: 'btn btn-link back', href: '#/members' }, T.backToMembers),

      el('div', { class: 'screen-head' },
        el('div', {},
          el('h1', { class: 'screen-title' }, member.full_name),
          el('p', { class: 'muted' }, [
            member.member_code,
            summary?.age_years ? `age ${summary.age_years}` : null,
            member.status === 'inactive' ? T.inactive : null,
          ].filter(Boolean).join(' · ')),
        ),
        el('a', { class: 'btn', href: `#/members/${memberId}/edit` }, T.edit),
      ),

      eligibilityBanner(eligibility),

      // Issue carries the member through, so the scanner is one tap away with
      // no second search (§5.1 step 2 is already answered).
      //
      // There is no Return button here. The books this child holds are listed
      // further down with a Return on each — sending staff to a scanner to
      // find a book already named on the screen in front of them is a longer
      // route to the same place.
      el('div', { class: 'action-row' },
        el('a', { class: 'btn btn-primary', href: `#/issue/${memberId}` }, T.issueToThisMember),
      ),

      // The fee is the single most common reason a child cannot borrow, and
      // recording it is a ten-second job — so when it is missing it gets a
      // full-width primary action rather than a link buried further down.
      unpaid && !showPaymentForm
        ? el('button', {
            class: 'btn btn-primary btn-large',
            onClick: () => { showPaymentForm = true; render(); },
          }, T.recordFee)
        : null,

      showPaymentForm && justCreated
        ? el('p', { class: 'notice' }, T.memberCreatedTakeFee)
        : null,

      showPaymentForm ? paymentForm() : null,

      eligibility ? slotsLine(eligibility.slots_used, eligibility.slots_total) : null,

      loans.length ? loansCard() : null,

      contactCard(),
      paymentsCard(),
      historyCard(),
      isAdmin() ? adminCard() : null,
    );
  }

  // Everything the child is holding, with a Return on each and a Return all
  // for the common case of a bag emptied onto the desk.
  function loansCard() {
    return el('section', { class: 'card' },
      el('div', { class: 'session-head' },
        el('h2', { class: 'card-title' }, T.currentLoans),
        loans.length > 1 && !pendingReturn
          ? el('button', {
              class: 'btn btn-sm', disabled: !!busy,
              onClick: () => { pendingReturn = 'all'; render(); },
            }, T.returnAll(loans.length))
          : null,
      ),
      pendingReturn ? confirmReturnCard() : null,
      ...loans.map((l) => loanRow(l, {
        onRenew: doRenew,
        onReturn: (loan) => { pendingReturn = loan; render(); },
        busy: !!busy || !!pendingReturn,
        renewing: busy === 'renew',
      })),
    );
  }

  // The confirmation, in the app's own vocabulary.
  //
  // This was a window.confirm(). A browser alert is the one piece of UI the app
  // cannot style: it arrives in the operating system's typeface with its own
  // buttons in its own order, and on a phone it lands over the middle of the
  // screen covering the very book being returned. An amber card in the card the
  // books are already in says the same thing in the same voice.
  function confirmReturnCard() {
    const all = pendingReturn === 'all';
    return el('div', { class: 'confirm-inline', role: 'alertdialog' },
      el('p', { class: 'confirm-inline-text' },
        all ? T.returnAllConfirm(loans.length) : T.returnConfirm(pendingReturn.title)),
      el('div', { class: 'form-actions' },
        el('button', {
          class: 'btn btn-primary', disabled: !!busy,
          onClick: () => (all ? doReturnAll() : doReturn(pendingReturn)),
        }, busy === 'return' ? T.returning : T.confirmReturn),
        el('button', {
          class: 'btn', disabled: !!busy,
          onClick: () => { pendingReturn = null; render(); },
        }, T.cancel),
      ),
    );
  }

  function contactCard() {
    const rows = [
      [T.guardianName, member.guardian_name],
      [T.guardianPhone, member.guardian_phone],
      [T.guardianPhone2, member.guardian_phone_2],
      [T.guardianEmail, member.guardian_email],
      [T.joined, member.joined_at ? formatDate(member.joined_at) : null],
      [T.notes, member.notes],
    ].filter(([, v]) => v);

    if (!rows.length) return null;
    return el('section', { class: 'card' },
      el('h2', { class: 'card-title' }, T.guardian),
      el('dl', { class: 'detail' }, ...rows.flatMap(([k, v]) => [
        el('dt', {}, k),
        // Phone numbers are tap-to-call: the overdue chase happens from a phone.
        el('dd', {}, /phone/i.test(k)
          ? el('a', { href: `tel:${String(v).replace(/\s/g, '')}` }, v)
          : v),
      ])),
    );
  }

  function paymentsCard() {
    return el('section', { class: 'card' },
      el('div', { class: 'session-head' },
        el('h2', { class: 'card-title' }, T.payments),
        !showPaymentForm
          ? el('button', { class: 'btn btn-sm', onClick: () => { showPaymentForm = true; render(); } }, T.addPayment)
          : null,
      ),
      payments.length
        ? el('div', {}, ...payments.map((p) => el('div', { class: 'loan-row' },
            el('span', { class: 'loan-title' }, money(p.amount)),
            el('span', { class: 'pill' }, T.kindShort[p.kind ?? 'membership'] ?? p.kind),
            // A discount is the reason the figure differs from the fee, so it
            // is stated rather than left to be inferred from a smaller number.
            p.discount_amount > 0
              ? el('span', { class: 'pill pill-warn' }, T.discountedBy(money(p.discount_amount)))
              : null,
            el('span', { class: 'muted' },
              [formatDate(p.paid_at),
               T.methods[p.method] ?? p.method,
               p.bank_account_name,
               p.receipt_no ? `#${p.receipt_no}` : null,
               p.recorded_by_name ? T.recordedBy(p.recorded_by_name) : null,
               p.notes].filter(Boolean).join(' · ')),
          )))
        : el('p', { class: 'empty' }, T.noPayments),
    );
  }

  // The payment form, shaped like the one staff already use in Happee SMS:
  //
  //   Fee        fixed, locked
  //   Discount   the ONE number that is typed
  //   To collect fee − discount, locked
  //
  // Nothing here computes what gets saved. The client sends the fee and the
  // discount; the database does the subtraction and stores the net (0010). Two
  // places doing the same arithmetic is how a discount ends up recorded as a
  // smaller fee, which loses the fact that a discount was given at all.
  function paymentForm() {
    // Membership is offered only if it has not been paid. The database enforces
    // one-per-member anyway (0006); omitting it here just avoids offering staff
    // an action that is guaranteed to fail.
    const alreadyMember = payments.some((p) => (p.kind ?? 'membership') === 'membership');
    const kindOptions = Object.entries(T.kinds)
      .filter(([k]) => k !== 'membership' || !alreadyMember);

    const kind = el('select', { class: 'text-input', 'aria-label': T.paymentKind },
      ...kindOptions.map(([v, label]) => el('option', { value: v }, label)));

    const fee = el('input', {
      type: 'number', class: 'text-input', min: '0', inputmode: 'numeric',
      value: settings?.membership_fee_amount ?? '', 'aria-label': T.feeCharged,
    });
    const feeHint = el('p', { class: 'muted' });

    const discount = el('input', {
      type: 'number', class: 'text-input', min: '0', inputmode: 'numeric',
      value: '0', 'aria-label': T.discount,
    });

    // Read-only rather than disabled: a disabled field is skipped by screen
    // readers and greys out the number staff are reading off to a parent.
    const net = el('input', {
      type: 'text', class: 'text-input input-locked', readonly: true,
      'aria-label': T.netPayable, tabindex: '-1',
    });

    const note = el('textarea', {
      class: 'text-input', rows: '2', 'aria-label': T.noteRequired, placeholder: T.noteRequired,
    });
    const noteWrap = el('div', { class: 'field' },
      el('label', { class: 'field-label' }, T.noteRequired), note,
      el('p', { class: 'muted' }, T.noteHint));

    const paidAt = el('input', { type: 'date', class: 'text-input', 'aria-label': T.paidOn });
    const method = el('select', { class: 'text-input', 'aria-label': T.method },
      ...Object.entries(T.methods).map(([v, label]) => el('option', { value: v }, label)));

    const bank = el('select', { class: 'text-input', 'aria-label': T.bankAccount },
      ...banks.map((b) => el('option', { value: b.id }, b.name)));
    const bankWrap = el('div', { class: 'field hidden' },
      el('label', { class: 'field-label' }, T.bankAccount), bank,
      el('p', { class: 'muted' }, T.bankAccountHint));

    const receipt = el('input', {
      type: 'text', class: 'text-input', 'aria-label': T.paymentReference,
      placeholder: T.paymentReference, autocomplete: 'off',
    });
    const receiptHint = el('p', { class: 'muted' }, T.referenceHint);

    // A membership fee is a fixed amount set for the branch — the database
    // ignores whatever is typed here and uses the configured figure, so the
    // field is locked rather than lying about being editable.
    function syncKind() {
      const isMembership = kind.value === 'membership';
      fee.readOnly = isMembership;
      fee.classList.toggle('input-locked', isMembership);
      if (isMembership) fee.value = settings?.membership_fee_amount ?? '';
      feeHint.textContent = isMembership ? T.amountFixed : '';
      noteWrap.classList.toggle('hidden', isMembership);
      note.required = !isMembership;
      syncTotals();
    }

    // The net, and whether a reference is needed for it. A fully discounted
    // payment collects nothing, so asking for a receipt number would be asking
    // staff to invent one.
    function syncTotals() {
      const gross = Math.max(0, Number(fee.value || 0));
      let off = Math.max(0, Number(discount.value || 0));
      if (off > gross) { off = gross; discount.value = String(gross); }
      const payable = gross - off;
      net.value = money(payable);
      receipt.required = payable > 0;
      receiptHint.textContent = payable > 0 ? T.referenceHint : T.referenceNotNeeded;
      receipt.classList.toggle('input-locked', payable === 0);
      receipt.readOnly = payable === 0;
      if (payable === 0) receipt.value = '';
    }

    // Which bank account only exists for a bank transfer, and the database
    // rejects it on anything else — so show it exactly when it applies.
    function syncMethod() {
      bankWrap.classList.toggle('hidden', method.value !== 'bank_transfer');
      bank.required = method.value === 'bank_transfer';
    }

    kind.addEventListener('change', syncKind);
    fee.addEventListener('input', syncTotals);
    discount.addEventListener('input', syncTotals);
    method.addEventListener('change', syncMethod);
    queueMicrotask(() => { syncKind(); syncMethod(); });

    // Default to today, in the school's timezone rather than the browser's.
    paidAt.value = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Yangon', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());

    async function save(e) {
      e.preventDefault();
      if (busy) return;
      busy = 'payment'; render();
      try {
        await recordPayment({
          memberId,
          kind: kind.value,
          amount: Number(fee.value || 0),
          discount: Number(discount.value || 0),
          paidAt: paidAt.value,
          method: method.value,
          bankAccountId: method.value === 'bank_transfer' ? bank.value : null,
          receiptNo: receipt.value.trim() || null,
          notes: note.value.trim() || null,
        });
        showPaymentForm = false; justCreated = false; busy = null;
        await load();
      } catch (err) {
        busy = null; render();
        flash(err.message);
      }
    }

    return el('form', { class: 'card card-warn', onSubmit: save },
      el('h2', { class: 'card-title' }, T.addPayment),
      // §4: this is RECORDED, not generated — staff copy the number from the
      // physical receipt book, same as Happee SMS.
      el('p', { class: 'muted' }, T.receiptHint),

      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.paymentKind), kind),

      el('div', { class: 'money-grid' },
        el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.feeCharged), fee, feeHint),
        el('div', { class: 'field' },
          el('label', { class: 'field-label' }, T.discount), discount,
          el('p', { class: 'muted' }, T.discountHint)),
        el('div', { class: 'field' },
          el('label', { class: 'field-label' }, T.netPayable), net,
          el('p', { class: 'muted' }, T.netFixed)),
      ),

      noteWrap,
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.paidOn), paidAt),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.method), method),
      bankWrap,
      el('div', { class: 'field' },
        el('label', { class: 'field-label' }, T.paymentReference), receipt, receiptHint),

      el('div', { class: 'form-actions' },
        el('button', { class: 'btn btn-primary', type: 'submit', disabled: busy === 'payment' },
          busy === 'payment' ? T.saving : T.savePayment),
        el('button', { class: 'btn', onClick: () => { showPaymentForm = false; justCreated = false; render(); } }, T.cancel),
      ),
    );
  }

  function historyCard() {
    const closed = history.filter((h) => h.status !== 'active');
    if (!closed.length) return null;
    return el('section', { class: 'card' },
      el('div', { class: 'session-head' },
        el('h2', { class: 'card-title' }, T.history),
        el('button', {
          class: 'btn btn-sm',
          onClick: () => { showHistory = !showHistory; render(); },
        }, showHistory ? T.hide : T.showCount(closed.length)),
      ),
      showHistory
        ? el('div', {}, ...closed.map((h) => el('div', { class: 'history-row' },
            el('span', { class: 'loan-title' }, h.title),
            // Both dates: the return date alone cannot answer how long the
            // child actually had the book, which is the question staff ask.
            el('span', { class: 'muted' },
              h.status === 'lost'
                ? T.markedLost
                : `${T.borrowedOn} ${formatDate(h.issued_at)} · ${T.returned} ${formatDate(h.returned_at)}`),
            // Who handled it, so a query about a loan reaches the right person.
            h.issued_by_name || h.returned_by_name
              ? el('span', { class: 'muted history-staff' },
                  [h.issued_by_name ? T.issuedBy(h.issued_by_name) : null,
                   h.returned_by_name && h.returned_by_name !== h.issued_by_name
                     ? T.takenBackBy(h.returned_by_name) : null,
                  ].filter(Boolean).join(' · '))
              : null,
          )))
        : null,
    );
  }

  function adminCard() {
    const active = member.status === 'active';
    return el('section', { class: 'card' },
      el('h2', { class: 'card-title' }, T.adminActions),
      el('p', { class: 'muted' }, active ? T.deactivateHint : T.reactivateHint),
      confirmingDeactivate ? deactivateCard() : el('button', {
        class: `btn ${active ? 'btn-danger' : 'btn-primary'}`,
        disabled: busy === 'status',
        onClick: () => toggleStatus(active),
      }, active ? T.deactivate : T.reactivate),
    );
  }

  // The last operating-system dialog in the app. window.confirm and alert were
  // both replaced with in-app cards (see confirmReturnCard above) for reasons
  // that apply here word for word: a prompt() arrives in the OS typeface, with
  // its own buttons in its own order, over the middle of the screen. This one
  // survived only because it needed a text field rather than a yes/no.
  function deactivateCard() {
    const reason = el('textarea', {
      class: 'text-input', rows: '2', 'aria-label': T.deactivateReasonPrompt,
      placeholder: T.deactivateReasonPrompt,
    });
    queueMicrotask(() => reason.focus());
    return el('div', { class: 'confirm-inline', role: 'alertdialog' },
      el('p', { class: 'confirm-inline-text' }, T.deactivateReasonPrompt),
      el('div', { class: 'field' }, reason),
      el('div', { class: 'form-actions' },
        el('button', {
          class: 'btn btn-danger', disabled: busy === 'status',
          onClick: () => doSetStatus('inactive', reason.value.trim() || null),
        }, busy === 'status' ? T.saving : T.deactivate),
        el('button', {
          class: 'btn', disabled: busy === 'status',
          onClick: () => { confirmingDeactivate = false; render(); },
        }, T.cancel),
      ),
    );
  }

  function toggleStatus(currentlyActive) {
    if (currentlyActive) { confirmingDeactivate = true; render(); return; }
    return doSetStatus('active', null);
  }

  async function doSetStatus(next, reason) {
    busy = 'status'; render();
    try {
      await setMemberStatus(memberId, next, reason || null);
      busy = null; confirmingDeactivate = false;
      await load();
    } catch (err) {
      busy = null; render();
      flash(err.message);
    }
  }

  // Returning from here needs no scan: the loan on screen already names the
  // copy. It is confirmed first, because unlike Renew it cannot be undone from
  // the app.
  async function doReturn(loan) {
    if (busy) return;
    busy = 'return'; render();
    try {
      const result = await returnCopy(loan.copy_id);
      emitDataChanged();
      busy = null; pendingReturn = null;
      await load();
      flash(result?.block_cleared
        ? `${T.returned}: ${loan.title}. ${T.blockCleared}`
        : `${T.returned}: ${loan.title}`, 'notice');
    } catch (err) {
      busy = null; pendingReturn = null; render();
      flash(err.message);
    }
  }

  // One at a time rather than in parallel: each return is its own transaction,
  // and a burst of concurrent calls makes a partial failure impossible to
  // report — staff would be told nothing came back when most of it did.
  async function doReturnAll() {
    if (busy) return;
    busy = 'return'; render();
    const queue = loans.slice();
    const done = [];
    let failure = null;
    for (const loan of queue) {
      try {
        await returnCopy(loan.copy_id);
        done.push(loan.title);
      } catch (err) { failure = err.message; break; }
    }
    emitDataChanged();
    busy = null; pendingReturn = null;
    await load();
    // Say what DID come back even when something stopped the run — the books
    // already returned are on the shelf whether or not the rest were.
    if (done.length) flash(T.returnedAll(done.length), 'notice');
    if (failure) flash(failure);
  }

  async function doRenew(loan) {
    if (busy) return;
    busy = 'renew'; render();
    try {
      await renewLoan(loan.loan_id ?? loan.id);
      emitDataChanged();
      busy = null;
      await load();
      flash(T.renewed(loan.title), 'notice');
    } catch (err) {
      busy = null; render();
      flash(err.message);
    }
  }

  function flash(message, kind = 'error') {
    const note = el('p', { class: kind === 'error' ? 'error' : 'notice', role: 'alert' }, message);
    view.querySelector('.banner')?.after(note);
    setTimeout(() => note.remove(), 6000);
  }

  load();
  return { destroy: () => {} };
}
